import React from 'react';
import { graphql } from 'gatsby';
import { GatsbySanityImageFluidNoBase64 } from '../../types';
import Img from 'gatsby-image/withIEPolyfill';
import clsx from 'clsx';
import backgroundWatermark from '../../assets/images/graphic_elements/circles_imageSection.svg';
import { replaceNewLinesWithBr } from '../../utils/sanity';
import { CommonSectionProps, SectionBgColor } from '../../templates/Page';
import { areSameFlatColor } from '../../utils/utils';
import { MEDIA_QUERIES_BREAK_POINTS } from '../../constants';
import { useWindowDimensions } from '../../utils/hooks';

import * as styles from './ImageSection.module.scss';

export const ImageSectionFragment = graphql`
  fragment ImageSection on SanityImageSection {
    title
    introText
    image {
      asset {
        fluid(maxWidth: 2220) {
          ...GatsbySanityImageFluid_noBase64
        }
      }
    }
    mobileImage {
      asset {
        fluid(maxWidth: 1456) {
          ...GatsbySanityImageFluid_noBase64
        }
      }
    }
    alt
    outroText
    withBackgroundWatermark
  }
`;

export type ImageSectionProps = {
  title?: string;
  introText?: string;
  image: {
    asset: {
      fluid: GatsbySanityImageFluidNoBase64;
    };
  };
  mobileImage?: {
    asset: {
      fluid: GatsbySanityImageFluidNoBase64;
    };
  };
  alt?: string;
  outroText?: string;
  withBackgroundWatermark?: boolean;
  className?: string;
};

export function getSectionBgColor(
  props: ImageSectionProps,
  windowWidth: number | null,
): SectionBgColor<'white'> {
  return {
    name: 'white',
    type:
      props.withBackgroundWatermark &&
      !props.introText &&
      !props.outroText &&
      windowWidth &&
      windowWidth >= MEDIA_QUERIES_BREAK_POINTS.tabletVertical
        ? 'textured'
        : 'solid',
  };
}

function ImageSection<PrevSectionColorName extends string>(
  props: ImageSectionProps & CommonSectionProps<PrevSectionColorName>,
): React.ReactElement {
  const {
    title,
    introText,
    image,
    mobileImage,
    alt,
    outroText,
    withBackgroundWatermark,
    className,
    previousSectionBgColor,
  } = props;

  const windowDimensions = useWindowDimensions();

  return (
    <section
      id={props.sectionID || ''}
      className={clsx(
        styles.sectionContainer,
        className,
        areSameFlatColor(
          getSectionBgColor(props, windowDimensions.width),
          previousSectionBgColor,
        ) && styles.noPaddingTop,
      )}
    >
      <div className={styles.section}>
        {title && <h2 className={styles.title}>{title}</h2>}
        {introText && <p className={styles.introText}>{replaceNewLinesWithBr(introText)}</p>}
        <div className={clsx(styles.desktopImageContainer, !mobileImage && styles.noMobileImage)}>
          <Img
            className={styles.image}
            loading="eager"
            fadeIn={false}
            fluid={image.asset.fluid}
            alt={alt || ''}
            objectFit="cover"
          />
          {withBackgroundWatermark && (
            <div className={styles.backgroundWatermarkContainer}>
              <img src={backgroundWatermark} alt="" />
            </div>
          )}
        </div>
        {mobileImage && (
          <div className={styles.mobileImageContainer}>
            <Img
              className={styles.mobileImage}
              loading="eager"
              fadeIn={false}
              fluid={mobileImage.asset.fluid}
              alt={alt || ''}
            />
          </div>
        )}

        {outroText && <p className={styles.outroText}>{replaceNewLinesWithBr(outroText)}</p>}
      </div>
    </section>
  );
}

export default ImageSection;
